import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, Link, Pagination, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import {
    Project, ProjectBudgetVsActual, ProjectLedgerPage, ProjectPl, ProjectStatus,
} from '../../interfaces/project';
import { ProjectService } from '../../services/project.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../utils/format';
import { firstOfYearLocalIso, formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import {
    ProjectFormDialog, ProjectStatusDialog, actionLabel, nextStatuses, statusColor, statusLabel,
} from './ProjectDialogs';

type TabKey = 'pl' | 'budget' | 'ledger' | 'tasks';
interface TaskRow { id: string; title: string; assigneeName?: string; status: string; dueDate?: string | null; priority: string }

const Tile: React.FC<{ label: string; value: string; danger?: boolean }> = ({ label, value, danger }) => (
    <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 160px', minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" sx={{ color: danger ? 'error.main' : undefined }}>{value}</Typography>
    </Paper>
);

/** P1-A: one project. PROJECT_VIEW holders see any project, the manager sees their own; the server 404s the rest. */
const ProjectDetail: React.FC = () => {
    const { id = '' } = useParams<{ id: string }>();
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.PROJECT_MANAGE);
    const [project, setProject] = useState<Project | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [tab, setTab] = useState<TabKey>('pl');
    const [from, setFrom] = useState(firstOfYearLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [applied, setApplied] = useState({ from: firstOfYearLocalIso(), to: toLocalIsoDate() });
    const [pl, setPl] = useState<ProjectPl | null>(null);
    const [bva, setBva] = useState<ProjectBudgetVsActual | null>(null);
    const [ledger, setLedger] = useState<ProjectLedgerPage | null>(null);
    const [ledgerPage, setLedgerPage] = useState(0);
    const [tasks, setTasks] = useState<TaskRow[]>([]);
    const [editing, setEditing] = useState(false);
    const [statusTo, setStatusTo] = useState<ProjectStatus | null>(null);

    const loadProject = useCallback(async () => {
        try {
            setProject(await ProjectService.get(id));
        } catch (err: any) {
            if (err?.response?.status === 404 || err?.response?.status === 403) setNotFound(true);
            else toast.showApiError(err, 'Failed to load the project');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { loadProject(); }, [loadProject]);

    useEffect(() => {
        if (!project) return;
        const range = { from: applied.from, to: applied.to };
        const fail = (msg: string) => (err: unknown) => toast.showApiError(err, msg);
        if (tab === 'pl') ProjectService.pl(project.id, range).then(setPl).catch(fail('Failed to load the project P&L'));
        if (tab === 'budget') ProjectService.budgetVsActual(project.id, range).then(setBva).catch(fail('Failed to load budget vs actual'));
        if (tab === 'ledger') {
            ProjectService.ledger(project.id, { ...range, page: ledgerPage, size: 50 }).then(setLedger)
                .catch(fail('Failed to load the project ledger'));
        }
        if (tab === 'tasks') ProjectService.tasks(project.id, { size: 100 }).then(p => setTasks(p.content ?? []))
            .catch(fail('Failed to load the tasks'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, tab, applied, ledgerPage]);

    if (notFound) return <Box sx={{ p: 2.5 }}><Alert severity="warning">Project not found.</Alert></Box>;
    if (!project) return <Box sx={{ p: 2.5 }}><Typography>Loading...</Typography></Box>;

    const margin = (project.actualRevenue ?? 0) - (project.actualCost ?? 0);
    return (
        <Box data-help-page="projects.detail.page" sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="h5">{project.code} {project.name}</Typography>
                <Chip size="small" label={statusLabel[project.status]} color={statusColor[project.status]} data-testid="project-status" />
                <Box sx={{ flex: 1 }} />
                {canManage && <Button data-help="projects.detail.action.edit" size="small" onClick={() => setEditing(true)}>Edit</Button>}
                {canManage && nextStatuses(project.status).map(s => (
                    <Button data-help="projects.detail.action.status" key={s} size="small" variant="outlined" color={s === 'CLOSED' ? 'warning' : 'primary'} onClick={() => setStatusTo(s)}>
                        {actionLabel(project.status, s)}
                    </Button>
                ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {project.customerName ? `Customer: ${project.customerName}. ` : ''}
                {project.managerName ? `Manager: ${project.managerName}. ` : ''}
                {project.startDate ? `From ${formatDate(project.startDate)}` : ''}{project.endDate ? ` to ${formatDate(project.endDate)}` : ''}
                {project.closedOn ? `. Closed on ${formatDate(project.closedOn)}` : ''}
            </Typography>
            {project.closeNote && <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{project.closeNote}</Alert>}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Tile label="Budget cost" value={project.budgetCost != null ? formatMoney(project.budgetCost) : '—'} />
                <Tile label="Actual cost to date" value={formatMoney(project.actualCost ?? 0)} />
                <Tile label="% of cost budget used" value={project.costUsedPct != null ? `${project.costUsedPct}%` : '—'}
                    danger={(project.costUsedPct ?? 0) > 100} />
                <Tile label="Revenue to date" value={formatMoney(project.actualRevenue ?? 0)} />
                <Tile label="Margin to date" value={formatMoney(margin)} danger={margin < 0} />
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
                <Tab data-help="projects.detail.tab.pl" value="pl" label="P&L" />
                <Tab data-help="projects.detail.tab.budget" value="budget" label="Budget vs actual" />
                <Tab data-help="projects.detail.tab.ledger" value="ledger" label="Ledger" />
                <Tab data-help="projects.detail.tab.tasks" value="tasks" label="Tasks" />
            </Tabs>

            {tab !== 'tasks' && (
                <Box data-help-section="projects.detail.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                    <TextField data-help="projects.detail.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                    <TextField data-help="projects.detail.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                    <Button data-help="projects.detail.apply" variant="outlined" onClick={() => { setLedgerPage(0); setApplied({ from, to }); }}>Apply</Button>
                    {tab === 'ledger' && (
                        <Button data-help="projects.detail.excel" variant="outlined" startIcon={<FileDownloadIcon />}
                            onClick={() => ProjectService.downloadLedger(project.id, applied, 'xlsx')
                                .catch(err => toast.showApiError(err, 'Download failed'))}>
                            Excel
                        </Button>
                    )}
                </Box>
            )}

            {tab === 'pl' && pl && (
                <TableContainer component={Paper} variant="outlined">
                    <Table data-help-section="projects.detail.plTable" size="small">
                        <TableHead><TableRow><TableCell data-help="projects.detail.pl.col.account">Account</TableCell><TableCell data-help="projects.detail.pl.col.type">Type</TableCell><TableCell data-help="projects.detail.pl.col.amount" align="right">Amount</TableCell></TableRow></TableHead>
                        <TableBody>
                            {pl.accounts.length === 0 && <TableRow><TableCell colSpan={3}>No postings tagged to this project in the period.</TableCell></TableRow>}
                            {pl.accounts.map(a => (
                                <TableRow key={a.accountId}>
                                    <TableCell>{a.accountCode ? `${a.accountCode} ` : ''}{a.accountName}</TableCell>
                                    <TableCell>{a.accountType === 'REVENUE' ? 'Revenue' : 'Expense'}</TableCell>
                                    <TableCell align="right">{formatMoney(a.amount)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow><TableCell colSpan={2}><b>Revenue</b></TableCell><TableCell align="right" data-testid="pl-revenue"><b>{formatMoney(pl.revenue)}</b></TableCell></TableRow>
                            <TableRow><TableCell colSpan={2}><b>Expense</b></TableCell><TableCell align="right"><b>{formatMoney(pl.expense)}</b></TableCell></TableRow>
                            <TableRow><TableCell colSpan={2}><b>Margin</b></TableCell><TableCell align="right"><b>{formatMoney(pl.margin)}</b></TableCell></TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {tab === 'budget' && bva && (
                <Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                        <Tile label="Budget cost (header)" value={bva.budgetCost != null ? formatMoney(bva.budgetCost) : '—'} />
                        <Tile label="Actual cost to date" value={formatMoney(bva.actualCostToDate)} />
                        <Tile label="Budget revenue (header)" value={bva.budgetRevenue != null ? formatMoney(bva.budgetRevenue) : '—'} />
                        <Tile label="Actual revenue to date" value={formatMoney(bva.actualRevenueToDate)} />
                    </Box>
                    {bva.variance ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table data-help-section="projects.detail.budgetTable" size="small">
                                <TableHead><TableRow><TableCell data-help="projects.detail.budget.col.account">Account</TableCell><TableCell data-help="projects.detail.budget.col.budget" align="right">Budget</TableCell><TableCell data-help="projects.detail.budget.col.actual" align="right">Actual</TableCell><TableCell data-help="projects.detail.budget.col.variance" align="right">Variance</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {bva.variance.rows.length === 0 && <TableRow><TableCell colSpan={4}>No budget lines or postings for this project in the period.</TableCell></TableRow>}
                                    {bva.variance.rows.map(r => (
                                        <TableRow key={r.accountId}>
                                            <TableCell>{r.accountCode} {r.accountName}</TableCell>
                                            <TableCell align="right">{formatMoney(r.budget)}</TableCell>
                                            <TableCell align="right">{formatMoney(r.actual)}</TableCell>
                                            <TableCell align="right">{formatMoney(r.variance)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info">No budget version has lines for this project; the header budget above is the comparison.</Alert>
                    )}
                </Box>
            )}

            {tab === 'ledger' && ledger && (
                <>
                    <TableContainer component={Paper} variant="outlined">
                        <Table data-help-section="projects.detail.ledgerTable" size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="projects.detail.ledger.col.date">Date</TableCell><TableCell data-help="projects.detail.ledger.col.entry">Entry</TableCell><TableCell data-help="projects.detail.ledger.col.account">Account</TableCell><TableCell data-help="projects.detail.ledger.col.description">Description</TableCell>
                                    <TableCell data-help="projects.detail.ledger.col.debit" align="right">Debit</TableCell><TableCell data-help="projects.detail.ledger.col.credit" align="right">Credit</TableCell><TableCell data-help="projects.detail.ledger.col.balance" align="right">Balance</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ledger.rows.length === 0 && <TableRow><TableCell colSpan={7}>No postings in the period.</TableCell></TableRow>}
                                {ledger.rows.map(r => (
                                    <TableRow key={r.lineId} data-testid="ledger-row">
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.date)}</TableCell>
                                        <TableCell>
                                            {ledger.canOpenEntries
                                                ? <Link component={RouterLink} to={`/accounting/journal-entries/${r.entryId}`} data-testid="ledger-entry-link">{r.entryNumber}</Link>
                                                : r.entryNumber}
                                        </TableCell>
                                        <TableCell>{r.accountCode ? `${r.accountCode} ` : ''}{r.accountName}</TableCell>
                                        <TableCell>{r.description ?? ''}</TableCell>
                                        <TableCell align="right">{r.debit ? formatMoney(r.debit) : ''}</TableCell>
                                        <TableCell align="right">{r.credit ? formatMoney(r.credit) : ''}</TableCell>
                                        <TableCell align="right">{r.runningBalance != null ? formatMoney(r.runningBalance) : ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {ledger.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination count={ledger.totalPages} page={ledgerPage + 1} onChange={(_, p) => setLedgerPage(p - 1)} />
                        </Box>
                    )}
                </>
            )}

            {tab === 'tasks' && (
                <TableContainer component={Paper} variant="outlined">
                    <Table data-help-section="projects.detail.tasksTable" size="small">
                        <TableHead><TableRow><TableCell data-help="projects.detail.tasks.col.title">Title</TableCell><TableCell data-help="projects.detail.tasks.col.assignee">Assignee</TableCell><TableCell data-help="projects.detail.tasks.col.priority">Priority</TableCell><TableCell data-help="projects.detail.tasks.col.status">Status</TableCell><TableCell data-help="projects.detail.tasks.col.due">Due</TableCell></TableRow></TableHead>
                        <TableBody>
                            {tasks.length === 0 && <TableRow><TableCell colSpan={5}>No tasks tagged to this project.</TableCell></TableRow>}
                            {tasks.map(t => (
                                <TableRow key={t.id}>
                                    <TableCell>{t.title}</TableCell><TableCell>{t.assigneeName ?? '—'}</TableCell>
                                    <TableCell>{t.priority}</TableCell><TableCell>{t.status}</TableCell>
                                    <TableCell>{t.dueDate ? formatDate(t.dueDate) : '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ProjectFormDialog open={editing} project={project} onClose={() => setEditing(false)}
                onSaved={p => { setEditing(false); setProject(p); }} />
            <ProjectStatusDialog project={statusTo ? project : null} target={statusTo} onClose={() => setStatusTo(null)}
                onDone={p => { setStatusTo(null); setProject(p); }} />
        </Box>
    );
};

export default ProjectDetail;
